import { PLOT_REFUND_RATE } from "./garden";

/**
 * FENCE STYLES — what one fence SEGMENT is made of. Styles are PER SEGMENT: the fence tool builds in the style
 * it has selected, and a garden can mix them (a stone wall along the front, pickets round the beds). The fence
 * MODEL is unchanged — sim/fence.ts still keeps a set of edge keys, and placement, collisions and demolish
 * never ask what a segment looks like. The style lives beside it in `fenceStyles` (edge key → style id), which
 * holds ONLY segments that are not the default, so an old save — and every rail fence — needs no entry.
 *
 * Three styles are REWARDS (`reward`): locked until an achievement unlocks "fence:<id>" (sim/cosmetics.ts, the
 * same derived gate the greenhouse skins use), after which they cost coins per segment like the rest.
 *
 * `height` is the style's top as a fraction of the rail fence's post (render/pixi/fenceArt.ts FENCE_POST_H) —
 * the build ghost's height. `price` is coins per segment; a demolished segment refunds PLOT_REFUND_RATE of it.
 * `cap` ranks how BULKY the member that caps a run's end is — 1 the slimmest (an iron post) to 6 the bulkiest (a
 * stone pier), the two wall styles' blocks above every post. Only the order matters: where two styles tie in depth
 * at a post it decides which cap draws in front (sim/fence.ts mixedTieWinner), so a slim post is not buried in a
 * pier, a hedge block or a fatter post.
 */
export type FenceStyleId = "rail" | "cottagePicket" | "bamboo" | "boxHedge" | "drystone" | "wroughtIron";

export interface FenceStyleDef {
  id: FenceStyleId;
  name: string;
  price: number;
  height: number;
  reward?: boolean;
  cap: number;
}

export const FENCE_STYLES: readonly FenceStyleDef[] = [
  { id: "rail",          name: "Weathered Rail",  price: 30, height: 1.0, cap: 4 },
  { id: "cottagePicket", name: "Cottage Picket",  price: 40, height: 0.86, cap: 3 },
  { id: "bamboo",        name: "Bamboo Screen",   price: 45, height: 1.04, cap: 2 },
  { id: "boxHedge",      name: "Box Hedge",       price: 60, height: 0.82, reward: true, cap: 5 },
  { id: "drystone",      name: "Dry-Stone Wall",  price: 70, height: 0.58, reward: true, cap: 6 },
  { id: "wroughtIron",   name: "Wrought Iron",    price: 90, height: 1.18, reward: true, cap: 1 },
];

export const DEFAULT_FENCE_STYLE: FenceStyleId = "rail";
export const FENCE_STYLE_IDS: readonly FenceStyleId[] = FENCE_STYLES.map((s) => s.id);

export function isFenceStyle(id: unknown): id is FenceStyleId {
  return typeof id === "string" && (FENCE_STYLE_IDS as readonly string[]).includes(id);
}
/** A style's definition (an unknown id reads as the default). */
export function fenceStyle(id: string | undefined): FenceStyleDef {
  return FENCE_STYLES.find((s) => s.id === id) ?? FENCE_STYLES[0]!;
}
/** The style a placed segment wears. */
export function styleOfFence(styles: Readonly<Record<string, string>> | undefined, key: string): FenceStyleId {
  const s = styles?.[key];
  return isFenceStyle(s) ? s : DEFAULT_FENCE_STYLE;
}
/** Coins a demolished segment of this style gives back — PLOT_REFUND_RATE of its price, rounded per segment. */
export function fenceRefund(id: string | undefined): number {
  return Math.round(fenceStyle(id).price * PLOT_REFUND_RATE);
}

/**
 * Coerce a saved `fenceStyles` map: known styles only, the default dropped (it is implied), and only keys that
 * are actually placed — a style entry for a segment that no longer stands is junk.
 */
export function coerceFenceStyles(raw: unknown, fences: readonly string[]): Record<string, FenceStyleId> {
  const out: Record<string, FenceStyleId> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const placed = new Set(fences);
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (placed.has(k) && isFenceStyle(v) && v !== DEFAULT_FENCE_STYLE) out[k] = v;
  }
  return out;
}
